/**
 * Coach account settings: profile & password, notification prefs (F3),
 * in-app notifications + dev email outbox, data export (A10/J8),
 * calendar feed (G4) and account deletion (A10).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { NOTIFICATION_TYPES } from '@solfo/shared';
import { api, getToken, ApiError } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import PushCard from '../../components/PushCard.tsx';
import { setLocale } from '../../lib/i18n.ts';
import {
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Tag,
  fmtDateTime,
} from '../../lib/ui.tsx';

interface NotificationPref {
  type: string;
  inApp: boolean;
  email: boolean;
  push: boolean;
}

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
}

interface OutboxRow {
  id: string;
  subject: string;
  bodyText: string;
  createdAt: string;
}

/** Downloads need the Authorization header → fetch as blob and save. */
async function downloadAuthed(path: string, filename: string) {
  const res = await fetch(path, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new ApiError(res.status, 'Download failed');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SettingsPage() {
  const { me, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const toast = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 3000);
  };

  // ── Account ────────────────────────────────────────────────────────────────
  const [name, setName] = useState(me?.name ?? '');
  const [locale, setLocaleState] = useState(me?.locale ?? 'en');
  useEffect(() => {
    if (me) {
      setName(me.name);
      setLocaleState(me.locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  const saveAccount = useMutation({
    mutationFn: () => api('/api/me', { method: 'PATCH', body: { name: name.trim(), locale } }),
    onSuccess: async () => {
      setError(null);
      setLocale(locale);
      await refresh();
      toast('Account saved');
    },
    onError: setError,
  });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const changePassword = useMutation({
    mutationFn: () => api('/api/me/password', { body: { currentPassword, newPassword } }),
    onSuccess: () => {
      setError(null);
      setCurrentPassword('');
      setNewPassword('');
      toast('Password changed');
    },
    onError: setError,
  });

  // ── F3 · Notification prefs ────────────────────────────────────────────────
  const prefsQuery = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api<NotificationPref[]>('/api/me/notification-prefs'),
  });
  const [prefs, setPrefs] = useState<NotificationPref[] | null>(null);
  useEffect(() => {
    if (prefsQuery.data && prefs === null) setPrefs(prefsQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsQuery.data]);

  const savePrefs = useMutation({
    mutationFn: (body: NotificationPref[]) => api('/api/me/notification-prefs', { method: 'PUT', body }),
    onSuccess: () => {
      setError(null);
      toast('Notification preferences saved');
      void qc.invalidateQueries({ queryKey: ['notification-prefs'] });
    },
    onError: setError,
  });

  const togglePref = (type: string, channel: 'inApp' | 'email' | 'push') =>
    setPrefs((p) => (p ?? []).map((row) => (row.type === type ? { ...row, [channel]: !row[channel] } : row)));

  // ── Dev email outbox ───────────────────────────────────────────────────────
  const [outboxOpen, setOutboxOpen] = useState(false);
  const outbox = useQuery({
    queryKey: ['email-outbox'],
    queryFn: () => api<OutboxRow[]>('/api/notifications/outbox'),
    enabled: outboxOpen,
  });

  // ── G4 · Calendar feed ─────────────────────────────────────────────────────
  const icsUrl = useQuery({
    queryKey: ['ics-url'],
    queryFn: () => api<{ url: string }>('/api/scheduling/ics-url'),
  });

  // ── A10 · Danger zone ──────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const deleteAccount = useMutation({
    mutationFn: () => api('/api/me', { method: 'DELETE', body: { password: deletePassword } }),
    onSuccess: () => {
      logout();
      navigate('/login');
    },
    onError: setError,
  });


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Settings</h1>
        {flash && <Tag tone="green">{flash}</Tag>}
      </div>

      {/* Account */}
      <Card title="Account">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Language">
            <Select value={locale} onChange={(e) => setLocaleState(e.target.value)}>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </Select>
          </Field>
          <Button disabled={!name.trim() || saveAccount.isPending} onClick={() => saveAccount.mutate()}>
            Save
          </Button>
        </div>
        <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <Field label="Current password">
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </Field>
          <Field label="New password" hint="At least 8 characters.">
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </Field>
          <Button
            variant="secondary"
            disabled={!currentPassword || newPassword.length < 8 || changePassword.isPending}
            onClick={() => changePassword.mutate()}
          >
            Change password
          </Button>
        </div>
      </Card>

      {/* F3 · Notification prefs */}
      <Card title="Notification preferences">
        {prefsQuery.isLoading || prefs === null ? (
          <Spinner />
        ) : (
          <>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 pr-3 font-medium">Event</th>
                  <th className="py-2 pr-3 text-center font-medium">In-app</th>
                  <th className="py-2 pr-3 text-center font-medium">Email</th>
                  <th className="py-2 text-center font-medium">Push</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_TYPES.map((type) => {
                  const row = prefs.find((p) => p.type === type) ?? { type, inApp: true, email: true, push: true };
                  return (
                    <tr key={type} className="border-b border-slate-100">
                      <td className="py-2 pr-3 capitalize">{type.replace(/_/g, ' ')}</td>
                      <td className="py-2 pr-3 text-center">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-600"
                          checked={row.inApp}
                          onChange={() => togglePref(type, 'inApp')}
                        />
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-600"
                          checked={row.email}
                          onChange={() => togglePref(type, 'email')}
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-600"
                          checked={row.push}
                          onChange={() => togglePref(type, 'push')}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Button className="mt-3" disabled={savePrefs.isPending} onClick={() => savePrefs.mutate(prefs)}>
              Save preferences
            </Button>
          </>
        )}
      </Card>

      {/* F3 · per-device push opt-in */}
      <PushCard />

      {/* Dev outbox */}
      <Card
        title="Recent emails (dev outbox)"
        actions={
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setOutboxOpen((o) => !o)}>
            {outboxOpen ? 'Hide' : 'Show'}
          </Button>
        }
      >
        {!outboxOpen ? (
          <p className="text-xs text-slate-500">In development, emails land here instead of your inbox.</p>
        ) : outbox.isLoading ? (
          <Spinner />
        ) : (outbox.data ?? []).length === 0 ? (
          <p className="text-xs text-slate-500">No emails yet.</p>
        ) : (
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {(outbox.data ?? []).map((m) => (
              <li key={m.id} className="rounded-lg bg-slate-50 p-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-sm font-medium">{m.subject}</p>
                  <span className="shrink-0 text-xs text-slate-400">{fmtDateTime(m.createdAt)}</span>
                </div>
                <p className="mt-1 text-xs whitespace-pre-wrap text-slate-600">{m.bodyText}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* G4 · Calendar feed */}
      <Card title="Calendar feed">
        <p className="mb-2 text-xs text-slate-500">
          Subscribe from Google or Apple Calendar — your lessons stay in sync automatically.
        </p>
        {icsUrl.isLoading ? (
          <Spinner />
        ) : icsUrl.data ? (
          <div className="flex items-center gap-2">
            <Input readOnly value={icsUrl.data.url} onFocus={(e) => e.currentTarget.select()} />
            <Button
              variant="secondary"
              className="shrink-0"
              onClick={() => {
                void navigator.clipboard.writeText(icsUrl.data.url);
                toast('Calendar URL copied');
              }}
            >
              Copy
            </Button>
          </div>
        ) : (
          <ErrorText error={icsUrl.error} />
        )}
      </Card>

      {/* A10/J8 · Data */}
      <Card title="Your data">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => void downloadAuthed('/api/me/export', 'solfo-export.json').catch(setError)}
          >
            Export everything (JSON)
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadAuthed('/api/analytics/export/roster.csv', 'solfo-roster.csv').catch(setError)}
          >
            Roster CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadAuthed('/api/analytics/export/progress.csv', 'solfo-progress.csv').catch(setError)}
          >
            Progress CSV
          </Button>
        </div>
      </Card>

      {/* A10 · Danger zone */}
      <Card title="Danger zone" className="border-red-200">
        <p className="mb-2 text-xs text-slate-500">
          Deleting your account scrubs your personal data. Invoices are retained as required by law.
        </p>
        <Button variant="danger" onClick={() => setDeleteOpen(true)}>
          Delete account…
        </Button>
      </Card>

      <ErrorText error={error} />

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete account">
        <p className="mb-3 text-sm text-slate-600">
          This cannot be undone. Enter your password to permanently delete your account.
        </p>
        <Field label="Password">
          <Input
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            autoFocus
          />
        </Field>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!deletePassword || deleteAccount.isPending} onClick={() => deleteAccount.mutate()}>
            Delete my account
          </Button>
        </div>
      </Modal>
    </div>
  );
}
